import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useRouter } from 'next/router';

import {
  cioFacetsToUrlFilters,
  urlFiltersToCioFacets,
} from '~/grid/utils/cioUtils';

import useGridInputs, { GRID_TYPES } from '../hooks/useGridInputs';

import { useGridFacets } from './GridDataContexts';

/**
 * StagedFiltersContext manages filter selections the user has toggled but not
 * yet applied to the URL. Users stage multiple facets, then commit all at once
 * via "Show Results". See docs/grid-provider-architecture.md "Staged Filters &
 * Disabled Option States" for the full design.
 */

export interface StagedFiltersContextValue {
  /** Current staged filter selections keyed by CIO facet name */
  stagedFilters: Record<string, string[]>;
  /** Toggle a single facet option on/off in the staged selections */
  toggleFilter: (params: { facetName: string; facetOption: string }) => void;
  /** Check if a specific facet option is currently staged */
  isFilterSelected: (params: {
    facetName: string;
    facetOption: string;
  }) => boolean;
  /** Clear all staged selections (sets every active facet to []) */
  clearAllFilters: () => void;
  /** Reset staged selections to match current URL filters */
  resetToUrl: () => void;
  /** Staged filters converted back to URL param names (for API queries) */
  stagedFiltersAsUrlParams: Record<string, string[]>;
  /** Staged sort key (mobile filter panel only — applied on Show Results) */
  stagedSort: string | null;
  /** Update the staged sort key */
  setStagedSort: (sort: string | null) => void;
}

export const StagedFiltersContext = React.createContext<
  StagedFiltersContextValue | undefined
>(undefined);

export const useStagedFilters = (): StagedFiltersContextValue => {
  const context = React.useContext(StagedFiltersContext);

  if (!context) {
    throw new Error(
      'useStagedFilters must be used within a PendingFiltersProvider'
    );
  }

  return context;
};

/**
 * Hook containing all staged filters state logic. Consumed by the provider
 * to keep the provider file thin.
 */
export const useStagedFiltersState = () => {
  const [stagedFilters, setStagedFilters] = useState<Record<string, string[]>>(
    {}
  );
  const [stagedSort, setStagedSort] = useState<string | null>(null);
  const { selectedUrlFilters } = useGridFacets();
  const { gridType } = useGridInputs();
  const router = useRouter();
  const urlSort = (router.query.sort as string) || null;

  const resetToUrl = useCallback(() => {
    const mapped = Object.entries(selectedUrlFilters).reduce(
      (acc, [facetName, facetOptions]) => {
        const mappedFacetName =
          gridType === GRID_TYPES.PRODUCTS_V2_GRID
            ? facetName
            : urlFiltersToCioFacets[facetName] || facetName;
        return { ...acc, [mappedFacetName]: facetOptions };
      },
      {}
    );
    setStagedFilters(mapped);
    setStagedSort(urlSort);
  }, [gridType, selectedUrlFilters, urlSort]);

  useEffect(() => {
    resetToUrl();
  }, [resetToUrl]);

  const toggleFilter = useCallback(
    ({
      facetName,
      facetOption,
    }: {
      facetName: string;
      facetOption: string;
    }) => {
      setStagedFilters((prev) => {
        if (!prev[facetName]) {
          return { ...prev, [facetName]: [facetOption] };
        }

        const isSelected = prev[facetName].some(
          (option) => String(option) === String(facetOption)
        );
        const newOptions = isSelected
          ? prev[facetName].filter(
              (option) => String(option) !== String(facetOption)
            )
          : [...prev[facetName], facetOption];

        return { ...prev, [facetName]: newOptions };
      });
    },
    []
  );

  const isFilterSelected = useCallback(
    ({
      facetName,
      facetOption,
    }: {
      facetName: string;
      facetOption: string;
    }) => {
      return Boolean(
        stagedFilters[facetName]?.some(
          (option) => String(option) === String(facetOption)
        )
      );
    },
    [stagedFilters]
  );

  const clearAllFilters = useCallback(() => {
    setStagedFilters((prev) =>
      Object.keys(prev).reduce(
        (acc, facetName) => ({ ...acc, [facetName]: [] }),
        {}
      )
    );
  }, []);

  const stagedFiltersAsUrlParams = useMemo(() => {
    return Object.entries(stagedFilters).reduce(
      (acc, [facetName, facetOptions]) => {
        const urlName = cioFacetsToUrlFilters[facetName] || facetName;
        return { ...acc, [urlName]: facetOptions };
      },
      {}
    );
  }, [stagedFilters]);

  return useMemo(
    () => ({
      stagedFilters,
      toggleFilter,
      isFilterSelected,
      clearAllFilters,
      resetToUrl,
      stagedFiltersAsUrlParams,
      stagedSort,
      setStagedSort,
    }),
    [
      stagedFilters,
      toggleFilter,
      isFilterSelected,
      clearAllFilters,
      resetToUrl,
      stagedFiltersAsUrlParams,
      stagedSort,
    ]
  );
};
